import { useState, type FormEvent } from "react";
import { channels } from "@/mocks/news";

const SUBMIT_URL = "https://readdy.ai/api/form/dah88mrp14a1h3mr6f3g";

type Status = "idle" | "submitting" | "success" | "error";

export default function TipForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    formData.delete("contact_alt");

    if (honeypot) {
      setStatus("success");
      setMessage("线索已提交，编辑部会尽快核对。感谢你的支持。");
      form.reset();
      setDetailLength(0);
      return;
    }

    setStatus("submitting");
    setMessage("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const success = response.ok && parsed?.code === "OK" && !isSpam;

      if (success) {
        setStatus("success");
        setMessage("线索已提交，编辑部会尽快核对。感谢你的支持。");
        form.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setMessage(
          isSpam
            ? "提交未通过校验，请检查填写内容后重试。"
            : serverMsg || "提交失败，请稍后重试或通过邮箱联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="tip-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
      noValidate
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">线索投稿</h3>
      <p className="mt-2 font-body text-sm leading-6 text-foreground-600">
        把你掌握的信息告诉我们。请尽量写明时间、涉及范围与已有材料，编辑部会按顺序核实。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs text-foreground-700">称呼 *</span>
          <input
            type="text"
            name="nickname"
            required
            maxLength={40}
            placeholder="如何称呼你"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs text-foreground-700">联系邮箱 *</span>
          <input
            type="email"
            name="email"
            required
            maxLength={80}
            placeholder="用于编辑部与你确认细节"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs text-foreground-700">线索频道</span>
          <select
            name="channel"
            defaultValue={channels[0].name}
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          >
            {channels.map((channel) => (
              <option key={channel.slug} value={channel.name}>
                {channel.name}
              </option>
            ))}
            <option value="其他">其他</option>
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-label text-xs text-foreground-700">线索标题</span>
          <input
            type="text"
            name="title"
            maxLength={60}
            placeholder="一句话概括这条线索"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="flex items-center justify-between font-label text-xs text-foreground-700">
          <span>线索详情 *</span>
          <span className="text-secondary-500">{detailLength}/500</span>
        </span>
        <textarea
          name="detail"
          required
          maxLength={500}
          rows={5}
          onChange={(event) => setDetailLength(event.target.value.length)}
          placeholder="请描述事件经过、时间节点与你掌握的材料（不超过 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-body text-sm leading-6 text-foreground-800 outline-none transition-colors focus:border-primary-400"
        ></textarea>
      </label>

      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="input-assist-line"
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-label text-xs text-secondary-500">
          提交即表示同意编辑部在核实后引用相关内容。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 dark:text-foreground-950"
        >
          <i className="ri-send-plane-fill" aria-hidden="true"></i>
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 font-label text-sm text-primary-700">
          <i className="ri-checkbox-circle-fill mt-0.5" aria-hidden="true"></i>
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-background-100 px-4 py-3 font-label text-sm text-foreground-800">
          <i className="ri-error-warning-fill mt-0.5 text-primary-600" aria-hidden="true"></i>
          {message}
        </p>
      )}
    </form>
  );
}